import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLang } from '../../i18n'
import { IconImage } from './ToolbarIcons'

/** What the blob store (and the document) accept — mirrors the old toolbar file input. */
export const IMAGE_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,image/avif,image/svg+xml'

/**
 * One dialog for the three ways an image gets into a part: paste a URL, drop a file on it, or
 * pick a file.
 *
 * It replaces two separate toolbar buttons ("Insert image by URL" 🖼 and "Upload an image" ⤴)
 * that sat side by side and offered no clue which was which. A member does not think "I want the
 * URL route" before they think "I want an image" — the route is a detail of the picture they
 * already have in hand, so it belongs inside the dialog, not in the toolbar.
 *
 * Presentational on purpose: the editor lives in EditorToolbar, so the two outcomes leave here as
 * callbacks. `busy` is owned by the caller too — it is the upload it started that is in flight.
 */
export function ImageInsertModal({
  busy = false,
  onInsertUrl,
  onFiles,
  onClose,
}: {
  /** An upload started by `onFiles` is still running — the dialog waits rather than closing. */
  busy?: boolean
  onInsertUrl: (url: string) => void
  onFiles: (files: File[]) => void
  onClose: () => void
}) {
  const { t } = useLang()
  const [url, setUrl] = useState('')
  const [dropping, setDropping] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    inputRef.current?.focus()
    return () => prev?.focus()
  }, [])

  const submitUrl = (): void => {
    const v = url.trim()
    if (v) onInsertUrl(v)
  }

  const take = (list: FileList | null): void => {
    const files = Array.from(list ?? []).filter((f) => f.type.startsWith('image/'))
    if (files.length > 0) onFiles(files)
  }

  return createPortal(
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className="modal img-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('img_insert_title')}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
      >
        <div className="modal-head">
          <h2>{t('img_insert_title')}</h2>
          <button className="modal-close" aria-label={t('close')} onClick={onClose}>✕</button>
        </div>
        <div className="modal-body img-modal-body">
          {/* Drop target AND file picker. Only the button opens the picker — a whole clickable
              div would be a control with no role and no keyboard path to it. */}
          <div
            className={'img-drop' + (dropping ? ' on' : '')}
            onDragOver={(e) => {
              if (!e.dataTransfer.types.includes('Files')) return
              e.preventDefault()
              setDropping(true)
            }}
            // dragleave bubbles from the children too, so "left the button for its own container"
            // must not read as "left the drop zone" — that flickers the highlight on and off.
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropping(false)
            }}
            onDrop={(e) => {
              setDropping(false)
              if (!e.dataTransfer.types.includes('Files')) return
              e.preventDefault()
              take(e.dataTransfer.files)
            }}
          >
            <IconImage className="img-drop-ico" />
            <p className="img-drop-lead">{busy ? t('img_uploading') : t('img_drop_label')}</p>
            <p className="img-drop-or">{t('img_drop_or')}</p>
            <button className="mini ghost" disabled={busy} onClick={() => fileRef.current?.click()}>
              {t('img_upload')}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept={IMAGE_ACCEPT}
              multiple
              hidden
              onChange={(e) => {
                take(e.target.files)
                e.target.value = '' // allow re-picking the same file
              }}
            />
          </div>

          <label className="prompt-label">
            {t('img_url_label')}
            <input
              ref={inputRef}
              className="prompt-input"
              value={url}
              placeholder={t('img_url_ph')}
              disabled={busy}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  submitUrl()
                }
              }}
            />
          </label>

          <div className="prompt-acts">
            <button className="mini ghost" onClick={onClose}>{t('cancel')}</button>
            <button className="mini ok" onClick={submitUrl} disabled={busy || !url.trim()}>
              {t('img_insert')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
